import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSearchParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DateInput } from '@/components/ui/DateInput'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SkeletonRows } from '@/components/ui/skeleton'
import { CLAIM_STATUS_LABELS, type ClaimRow, ClaimsTable } from '../../../../components/ClaimsTable'
import { formatDateForApi } from '../../../../lib/utils'
import { ClaimStatus, type InternalClaim, useInternalClaims } from '../../../../models/claim'
import { useFeatureFlag } from '../../../../models/flag'
import { ClaimAdjusterDialog } from './ClaimAdjusterDialog'
import { ClaimLifecycleActions } from './ClaimLifecycleActions'
import { ClaimLifecycleLinks } from './ClaimLifecycleLinks'
import { ClaimOutcomeDialog } from './ClaimOutcomeDialog'

export function InternalClaims() {
  const [searchParams, setSearchParams] = useSearchParams()
  const lifecycleEnabled = useFeatureFlag('ENABLE_CLAIM_LIFECYCLE')
  // Held here so a list refetch cannot unmount it with staff mid-entry.
  const [carrierFor, setCarrierFor] = useState<InternalClaim | null>(null)
  const [outcomeFor, setOutcomeFor] = useState<InternalClaim | null>(null)

  const page = Number(searchParams.get('page') || '1')
  const status = (searchParams.get('status') || '') as ClaimStatus | ''
  const dateFrom = searchParams.get('date_from') || ''
  const dateTo = searchParams.get('date_to') || ''

  function updateParams(updates: Record<string, string | undefined>) {
    const params: Record<string, string> = {}
    if (status) params.status = status
    if (dateFrom) params.date_from = dateFrom
    if (dateTo) params.date_to = dateTo
    for (const [key, value] of Object.entries(updates)) {
      if (value) {
        params[key] = value
      } else {
        delete params[key]
      }
    }
    if (!('page' in updates)) {
      delete params.page
    }
    setSearchParams(params)
  }

  const { claims, count, hasNextPage, hasPreviousPage, isLoading } = useInternalClaims({
    page,
    status: status || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  })

  const tableRows = claims.map((c: InternalClaim) => ({ ...c }))
  const claimFor = (row: ClaimRow) => claims.find((c: InternalClaim) => c.id === row.id)

  function goToPage(p: number) {
    updateParams({ page: p > 1 ? String(p) : undefined })
  }

  return (
    <>
      <Helmet>
        <title>Claims</title>
      </Helmet>
      <div className="flex flex-col gap-5">
        <div className="mb-2 text-2xl font-semibold">Claims</div>
        <Card>
          <CardHeader className="px-4 sm:px-6">
            <CardTitle>All Claims</CardTitle>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <Select
                value={status || 'all'}
                onValueChange={(v) => updateParams({ status: v === 'all' ? undefined : v })}
              >
                <SelectTrigger className="w-full border-neutral-300 data-[size=default]:h-11 sm:w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {Object.values(ClaimStatus).map((s) => (
                    <SelectItem key={s} value={s}>
                      {CLAIM_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* One range, one row: each field's placeholder already says which end it is. */}
              <div className="flex items-center gap-2">
                <div className="flex-1 sm:w-[160px] sm:flex-none">
                  <DateInput
                    label="From"
                    inputProps={{ 'aria-label': 'Claims from date' }}
                    value={dateFrom ? new Date(dateFrom) : undefined}
                    onChange={(date) =>
                      updateParams({
                        date_from: date ? (formatDateForApi(date.toISOString()) ?? undefined) : undefined,
                      })
                    }
                    hidden={dateTo ? [{ after: new Date(dateTo) }] : undefined}
                  />
                </div>
                <span aria-hidden className="text-muted-foreground">
                  –
                </span>
                <div className="flex-1 sm:w-[160px] sm:flex-none">
                  <DateInput
                    label="To"
                    inputProps={{ 'aria-label': 'Claims to date' }}
                    value={dateTo ? new Date(dateTo) : undefined}
                    onChange={(date) =>
                      updateParams({
                        date_to: date ? (formatDateForApi(date.toISOString()) ?? undefined) : undefined,
                      })
                    }
                    hidden={dateFrom ? [{ before: new Date(dateFrom) }] : undefined}
                  />
                </div>
              </div>

              {(status || dateFrom || dateTo) && (
                <Button
                  variant="link"
                  onClick={() =>
                    updateParams({
                      status: undefined,
                      date_from: undefined,
                      date_to: undefined,
                    })
                  }
                >
                  Clear filters
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            {isLoading ? (
              <SkeletonRows rows={8} />
            ) : (
              <>
                <ClaimsTable
                  rowActions={
                    lifecycleEnabled
                      ? (row) => {
                          const claim = claimFor(row)
                          return claim ? (
                            <ClaimLifecycleLinks claim={claim} onOpenCarrier={setCarrierFor} />
                          ) : null
                        }
                      : undefined
                  }
                  renderStatus={
                    lifecycleEnabled
                      ? (row) => {
                          const claim = claimFor(row)
                          return claim ? (
                            <ClaimLifecycleActions
                              claim={claim}
                              onOpenCarrier={setCarrierFor}
                              onAskOutcome={setOutcomeFor}
                            />
                          ) : null
                        }
                      : undefined
                  }
                  claims={tableRows}
                  showCustomer
                  emptyMessage="No claims found."
                  getEditHref={(claim) => `/insure/internal/claims/${claim.id}?org_id=${claim.orgId}`}
                />
                {(hasPreviousPage || hasNextPage) && (
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-muted-foreground text-base">
                      Page {page} ({count} total)
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!hasPreviousPage}
                        onClick={() => goToPage(page - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!hasNextPage}
                        onClick={() => goToPage(page + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {carrierFor && (
        <ClaimAdjusterDialog claim={carrierFor} open onOpenChange={(open) => !open && setCarrierFor(null)} />
      )}

      {outcomeFor && (
        <ClaimOutcomeDialog claim={outcomeFor} open onOpenChange={(open) => !open && setOutcomeFor(null)} />
      )}
    </>
  )
}
